// `chrome-extension.extension-tabs.client-contacts.client-contact.profile` — one contact's
// profile, opened by id with no alert behind it (plan §7 phase 4).
//
// The candidate profile's twin, and the differences are the ones `ClientContactSnippet`
// documents: no preview cards at all, an ATS card that opens expanded and reads different
// fields, and one action rather than five.
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useLocation, useNavigate, useOutlet, useSearchParams } from 'react-router-dom'
import {
  favorClientContacts,
  fetchViewModel,
  getClientContactByAlertIdIgnoreFilters,
  getClientContactEmailIntegrationDetails,
  getClientContactsIgnoreFiltersAlertless,
  hideClientContacts,
  unfavorClientContact,
  unhideClientContact,
  type AlertClientContactProfile,
  type ExportListTarget,
} from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import {
  applyFavor,
  applyHide,
  applyUnfavor,
  applyUnhide,
  ClientContactAsyncContainer,
  clientContactName,
  ClientContactSnippet,
  hasContactDetails,
  isCreateClientContactEnabled,
  isCreateNewCompanyDisabled,
  isUpdateClientContactInAtsSupported,
  ProfileAction,
  profileActions,
  useCreateClientContactInAts,
  type CreateInAtsOpenIntent,
  type ProfileActionDefinition,
  getAtsLabel,
  exportTitle,
  exportToExistingListEnabled,
  singleProfileExportEnabled,
  singleProfileNewListTarget,
  SingleProfileExportModal,
  type SingleProfileExportOutcome,
} from '@woo-ext/ui'
import { panelBase } from './panelBase.ts'
import { createInAtsTracking, trackClientContact, trackClientContactSnippet } from './actionTracking.ts'
import { publishPageEntity } from '../pageEntity.ts'
import { useProfileScrollTracking } from './useProfileScrollTracking.ts'
import { emitShortlistFlagEvent, requestShortlistExport, requestShortlistMessage } from './shortlistEvents.ts'
import { sendOpenHotlist } from './assignToJob.ts'
import { isInViewMore, viewLessQuery, viewMoreQuery } from './profileViewMore.ts'
import { publishTabName } from './tabNames.ts'

const SHORTLIST_MESSAGE_CANDIDATES = 'SHORTLIST_MESSAGE_CANDIDATES'
const EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED = 'EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED'
const SEND_CANDIDATE_DETAILS_TO_USER = 'SEND_CANDIDATE_DETAILS_TO_USER'

/** History entries whose carried Create-in-ATS intent has already opened the form. */
const consumedCreateInAtsIntents = new Set<string>()
/** The More header's "Get phone and email" CTA hangs off this one, and off nothing else. */
const UPDATE_CONTACT_DETAILS_ENABLED = 'UPDATE_CONTACT_DETAILS_ENABLED'
/** `FeatureFlags.HideClientContactContactDetails` — masks email and phone by their source. */
const HIDE_CLIENT_CONTACT_CONTACT_DETAILS = 'HIDE_CLIENT_CONTACT_CONTACT_DETAILS'

export interface ClientContactProfileProps {
  /**
   * `shortlistSessionInfo.viaHunter` — this profile opened from a search's contact list rather
   * than on its own.
   *
   * ⚠️ IT DECIDES WHICH CALL FETCHES THE CONTACT, NOT JUST WHICH ICONS SHOW.
   * `EntityContainerBase.getEntity` branches on `angular.isNumber($stateParams.alertId)`: with
   * an alert it asks `getClientContactByAlertIdIgnoreFilters`, without one the alertless call.
   * The alert-scoped answer carries this contact's standing WITHIN that search — hidden,
   * favorited, viewed — and the strip's Favor and Archive icons read exactly those fields, so
   * fetching alertlessly here would render the two icons against values that are always absent.
   */
  viaHunter?: boolean
}

export function ClientContactProfile({ viaHunter = false }: ClientContactProfileProps = {}): ReactElement {
  const [params] = useSearchParams()
  const onMoreRoute = useLocation().pathname.endsWith('/profile/more')

  const [contact, setContact] = useState<AlertClientContactProfile | undefined>(undefined)
  const [atsName, setAtsName] = useState<string | undefined>(undefined)
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  const [createInAtsEnabled, setCreateInAtsEnabled] = useState(false)
  const [updateInAtsSupported, setUpdateInAtsSupported] = useState(false)
  const [createNewCompanyDisabled, setCreateNewCompanyDisabled] = useState(false)
  const [updateContactDetailsEnabled, setUpdateContactDetailsEnabled] = useState(false)
  const [hideContactDetailsEnabled, setHideContactDetailsEnabled] = useState(false)
  const [actions, setActions] = useState<readonly ProfileActionDefinition[]>([])
  const [loadingAtsInfo, setLoadingAtsInfo] = useState(true)

  const raw = params.get('clientContactId')
  const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10)
  const contactId = Number.isNaN(parsed) ? undefined : parsed

  const rawAlert = params.get('alertId')
  const parsedAlert = rawAlert === null ? Number.NaN : Number.parseInt(rawAlert, 10)
  const alertId = Number.isNaN(parsedAlert) ? undefined : parsedAlert

  // The same params comparison as the candidate profile gates the More view; see that file.
  const rawAlgoScore = params.get('algoScore')
  const viewMore = isInViewMore(onMoreRoute, rawAlgoScore)
  /**
   * SINGLE_PROFILE_EXPORT (cee70a51e8) — the candidate profile's, for the contact: the picker at
   * `…/profile/export` when the ATS has existing lists, else straight to a new list named after the
   * contact. `onExport` in the outlet context is the panel's `OnClientContactsExport`; the PROFILE
   * runs it and owns the outcome (see `CandidateProfile.runExport`).
   */
  const [exportOpensPicker, setExportOpensPicker] = useState(false)
  const [exporting, setExporting] = useState<{ key: number; target: ExportListTarget } | undefined>(undefined)
  const runExport = (target: ExportListTarget): void => {
    setExporting((previous) => ({ key: (previous?.key ?? 0) + 1, target }))
  }
  const child = useOutlet(
    contact === undefined ? undefined : { checked: [{ id: contact.id, name: contact.name }], onExport: runExport },
  )

  const navigate = useNavigate()
  const location = useLocation()
  const { pathname: currentPath, search: currentSearch } = location

  /** Both of these are the candidate profile's, verbatim — see that file for why. */
  /** Answers whether it navigated; `state` rides along (see `createInAtsIntent` below). */
  const onToggleViewMore = (state?: unknown): boolean => {
    const query = new URLSearchParams(currentSearch)
    // ⚠️ THE PREDICATE IS `viewMore`, NOT THE PATH, and the difference is visible on the very
    // URL the fixtures use. `toggleViewMore` asks `isInViewMoreState()` — the same params
    // comparison that decides whether the button reads "More" or "Less" — so on
    // `…/profile/more` with no `algoScore` the button still says "More" and must still go
    // FORWARD. Keying the toggle on the path made it read "More" and act as "Less".
    if (viewMore) {
      // `originSnippet` stays: `$state.go` inherits it (`profileViewMore.ts`).
      void navigate(`${currentPath.replace(/\/more$/, '')}?${viewLessQuery('clientContactId', query)}`)
      return true
    }
    const base = onMoreRoute ? currentPath : `${currentPath}/more`
    const target = `${base}?${viewMoreQuery('clientContactId', query)}`
    // ui-router ignores a transition to the state it is already in; `navigate` does not.
    if (target === `${currentPath}${currentSearch}`) return false
    void navigate(target, state === undefined ? undefined : { state })
    return true
  }

  /**
   * ⚠️ THE CONTACT STRIP'S ONE ICON WAS INERT. This route passed no `onAction` at all, so the
   * single envelope — the only action the contact snippet offers — rendered with a pointer
   * cursor and did nothing. `onSingleClientContactMessageClick` resolves the mailbox first and
   * routes to the composer or to `…/email-signin`, exactly as the candidate's does.
   */
  const onExportSettled = (outcome: SingleProfileExportOutcome): void => {
    setExporting(undefined)
    // A rejected start stays on the picker for a retry (eace2a0e4b).
    if (outcome === 'failed') return
    if (outcome === 'exported') {
      setContact((previous) => (previous === undefined ? previous : { ...previous, exported: true }))
    }
    const profilePath = panelBase(currentPath)
    if (profilePath !== currentPath) void navigate(`${profilePath}${currentSearch}`)
  }

  const onProfileAction = (action: ProfileActionDefinition): void => {
    if (action.name === ProfileAction.Export) {
      if (contact === undefined) return
      // Inside the review the LIST exports it (`OnSingleClientContactExportClick` →
      // `onExportClientContact`), ticking the contact as the row's Export does.
      if (viaHunter) {
        if (alertId !== undefined) requestShortlistExport({ entity: 'clientContacts', alertId, row: contact })
        return
      }
      trackClientContact.exportClicked(contact, 'profile')
      if (exportOpensPicker) {
        void navigate(`${panelBase(currentPath)}/export${currentSearch}`)
        return
      }
      runExport(singleProfileNewListTarget(contact.name))
      return
    }
    if (action.name === ProfileAction.Favor || action.name === ProfileAction.Archive) {
      onFlagAction(action.name)
      return
    }
    if (action.name !== ProfileAction.Message) return
    // Inside the review the LIST messages it (`onSingleClientContactMessageClick` → its
    // `messageClientContact`): selection, tracking and the composer are the review's.
    if (
      viaHunter &&
      alertId !== undefined &&
      contact !== undefined &&
      requestShortlistMessage({ entity: 'clientContacts', alertId, row: contact })
    ) {
      return
    }
    if (contact !== undefined) trackClientContact.message(contact)
    // The same rule as the candidate profile's: inside the contacts review's side view the
    // composer is the REVIEW's (`ClientContactHunterReviewContactMessages`, `:219`), a sibling
    // of the profile; standalone, it is the profile's own child.
    const base = panelBase(currentPath)
    void getClientContactEmailIntegrationDetails().then(
      (details) => {
        const query = new URLSearchParams(currentSearch)
        if (details.enabled) {
          void navigate(`${base}/client-contact-messages?${query.toString()}`)
          return
        }
        if (details.emailServerType !== null) query.set('emailServerType', details.emailServerType)
        void navigate(`${base}/email-signin?${query.toString()}`)
      },
      () => undefined,
    )
  }

  /**
   * Favor and Archive — the two the strip gains inside the review (`viaHunter`), and they did
   * nothing here. `favorClientContact` / `hideClientContact` on the hunter actions service: the
   * same four calls as the review's row, and the same `OnClientContact*` events, so the row behind
   * this profile follows.
   */
  const onFlagAction = (name: typeof ProfileAction.Favor | typeof ProfileAction.Archive): void => {
    if (contact === undefined || alertId === undefined) return
    const id = contact.id
    if (name === ProfileAction.Favor) {
      const undo = contact.favorite === true
      if (undo) trackClientContact.unfavor(contact, 'profile')
      else trackClientContact.favor(contact, 'profile')
      void Promise.resolve(undo ? unfavorClientContact(alertId, id) : favorClientContacts(alertId, [id])).then(
        () => {
          setContact((previous) =>
            previous === undefined ? previous : undo ? applyUnfavor(previous) : applyFavor(previous),
          )
          emitShortlistFlagEvent({ entity: 'clientContacts', alertId, ids: [id], kind: undo ? 'unfavor' : 'favor' })
        },
        () => undefined,
      )
      return
    }
    if (contact.hidden === true) {
      trackClientContact.unhide(contact, 'profile')
      void unhideClientContact(alertId, id).then(
        () => {
          setContact((previous) => (previous === undefined ? previous : applyUnhide(previous)))
          emitShortlistFlagEvent({ entity: 'clientContacts', alertId, ids: [id], kind: 'unhide' })
        },
        () => undefined,
      )
      return
    }
    trackClientContact.hide(contact, 'profile')
    void hideClientContacts(alertId, [id]).then(
      (hiddenDate) => {
        setContact((previous) => (previous === undefined ? previous : applyHide(previous, hiddenDate)))
        emitShortlistFlagEvent({ entity: 'clientContacts', alertId, ids: [id], kind: 'hide', hiddenDate })
      },
      () => undefined,
    )
  }

  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        setAtsName(model.atsName ?? undefined)
        setAtsLabel(getAtsLabel(model))
        setExportOpensPicker(exportToExistingListEnabled(model))
        setCreateInAtsEnabled(isCreateClientContactEnabled(model.featureFlags, model.createClientContactInAtsEnabled))
        setUpdateInAtsSupported(isUpdateClientContactInAtsSupported(model.featureFlags, model.updateClientContactInAtsEnabled))
        setCreateNewCompanyDisabled(isCreateNewCompanyDisabled(model.featureFlags))
        // ⚠️ THE FLAG ALONE, with no `careero.*` companion beside it — unlike the one above,
        // which needs both. Upstream reads `careero.featureFlags.UPDATE_CONTACT_DETAILS_ENABLED`
        // straight into the controller.
        setUpdateContactDetailsEnabled(model.featureFlags?.[UPDATE_CONTACT_DETAILS_ENABLED] === true)
        setHideContactDetailsEnabled(model.featureFlags?.[HIDE_CLIENT_CONTACT_CONTACT_DETAILS] === true)
        setActions(
          profileActions({
            // ⚠️ THE CONTACT STRIP IS ONE ICON WIDE IN THE REFERENCE, and it is the same
            // provider that builds the candidate's five — the difference is which handlers the
            // contact snippet passes it. Download and Report have no contact equivalent, and
            // add-to-job is a candidate concept, so only the envelope survives.
            downloadWooProfileEnabled: false,
            viaHunter,
            messageCandidatesEnabled: model.featureFlags?.[SHORTLIST_MESSAGE_CANDIDATES] === true,
            sendCandidateDetailsEnabled: false,
            reportEnrichmentEnabled: false,
            // The contact snippet wires `exportToAtsList` too (cee70a51e8); the flag decides.
            singleProfileExportEnabled: singleProfileExportEnabled(model),
            exportTitle: exportTitle(model.atsName ?? undefined),
            atsLabel: getAtsLabel(model),
          }),
        )
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    if (contactId === undefined) return undefined
    let live = true
    const loading =
      viaHunter && alertId !== undefined
        ? getClientContactByAlertIdIgnoreFilters({ alertId, clientContactId: contactId })
        : getClientContactsIgnoreFiltersAlertless({ limitClientContactIds: [contactId] })
    loading.then(
      (page) => {
        if (!live) return
        const loaded = page.clientContactPage[0]
        setContact(loaded)
        setLoadingAtsInfo(false)
        publishTabName('clientContact', loaded === undefined ? 'Contact profile' : clientContactName(loaded))
      },
      () => {
        if (!live) return
        setLoadingAtsInfo(false)
        publishTabName('clientContact', 'Contact profile')
      },
    )
    return () => {
      live = false
    }
  }, [contactId, alertId, viaHunter])

  // The page view reads this page's contact off its container upstream; here the profile hands
  // it over (`pageEntity.ts`).
  useEffect(() => {
    if (contact !== undefined) publishPageEntity('clientContact', contact)
  }, [contact])

  /** The snippet's `alertId` binding — the review's side view passes it, the standalone profile does not. */
  const snippetAlertId = viaHunter ? alertId : undefined

  /**
   * The state's `client-contact-async-container` binding: `clientContactProfileAsyncContainer` on
   * the standalone profile (this contact's pending operations), `clientContactAlertAsyncContainer`
   * inside the review (the alert's).
   *
   * ⚠️ INSIDE THE REVIEW UPSTREAM, THE LIST OWNS THIS — it is resolved on the review state, the
   * list starts its poll (`initPendingEventsInterval`) and adds its page's contacts
   * (`addToPendingEventsByClientContactIds(this.page)`); the profile only subscribes. This port's
   * list has no container, so the profile builds the alert's and adds itself, which is the one
   * contact on that page the snippet can show. The poll's request is the same; what is not
   * reproduced is the list rows' GET_LATEST_DETAILS merge, which has no consumer yet.
   */
  const [asyncSetup, setAsyncSetup] = useState<{ container: ClientContactAsyncContainer; ready: Promise<unknown> } | undefined>(
    undefined,
  )
  useEffect(() => {
    if (contactId === undefined) return undefined
    const container = new ClientContactAsyncContainer()
    const init =
      viaHunter && alertId !== undefined
        ? container.initForAlert(alertId).then(() => container.addToPendingEventsByClientContactIds([contactId]))
        : container.initForClientContact(contactId)
    // A failed init leaves the list empty; upstream's resolver would fail the transition instead.
    setAsyncSetup({ container, ready: init.catch(() => undefined) })
    container.startPolling()
    return () => {
      container.destroy()
    }
  }, [contactId, alertId, viaHunter])

  // "Create in ATS" from the closed view opens More and then the form; the profile is rebuilt by
  // that navigation (see `onToggleViewMore` on the hook), so the form's intent travels in the
  // location's state and is consumed once per history entry — Back onto it does not reopen it.
  const navState: unknown = location.state
  const carried =
    typeof navState === 'object' && navState !== null && 'createInAts' in navState
      ? (navState as { createInAts?: CreateInAtsOpenIntent }).createInAts
      : undefined
  const createInAtsIntent = carried !== undefined && !consumedCreateInAtsIntents.has(location.key) ? carried : undefined
  const locationKey = location.key
  const onCreateInAtsIntentConsumed = useCallback(() => {
    consumedCreateInAtsIntents.add(locationKey)
  }, [locationKey])

  const createInAts = useCreateClientContactInAts({
    contact,
    alertId: snippetAlertId,
    atsName,
    atsLabel,
    updateSupported: updateInAtsSupported,
    createNewCompanyDisabled,
    updateContactDetailsEnabled,
    contactDetailsCtaLabel: hasContactDetails(contact) ? 'Find newer details' : 'Get phone and email',
    asyncContainer: asyncSetup?.container,
    asyncContainerReady: asyncSetup?.ready,
    viewMore,
    onToggleViewMore: (intent) => onToggleViewMore({ createInAts: intent }),
    openIntent: createInAtsIntent,
    onIntentConsumed: onCreateInAtsIntentConsumed,
    tracking: createInAtsTracking(snippetAlertId),
    // `notifyMe` closes the snippet only where it is not the standalone profile
    // (`snippetState !== ExtensionState.ClientContactProfile`): back to the review.
    ...(viaHunter && alertId !== undefined
      ? {
          onNotifyMeCloseSnippet: () => {
            void navigate(`/chrome-extension/extension/client-contact/review?alertId=${String(alertId)}`)
          },
        }
      : {}),
  })
  const onContentScroll = useProfileScrollTracking(contact, contact?.id, (current, metrics) => {
    trackClientContactSnippet.profileScroll(current, snippetAlertId, metrics)
  })

  return (
    <div className="shortlist-candidate-profile">
      <ClientContactSnippet
        {...(contact === undefined ? {} : { contact })}
        actions={actions}
        onAction={onProfileAction}
        onSocialUrlClick={() => {
          if (contact !== undefined) trackClientContactSnippet.socialUrl(contact, snippetAlertId)
        }}
        onContentScroll={onContentScroll}
        atsName={atsName}
        {...(atsLabel === undefined ? {} : { atsLabel })}
        createInAtsEnabled={createInAtsEnabled}
        createInAtsDisabled={createInAts.buttonDisabled}
        createInAtsTooltip={createInAts.buttonTooltip}
        onCreateInAts={createInAts.onCreateInAts}
        updateContactDetailsEnabled={updateContactDetailsEnabled}
        hideContactDetailsEnabled={hideContactDetailsEnabled}
        loadingSummaryAndPreview={loadingAtsInfo}
        // ⚠️ THE "More ›" BUTTON IS HIDDEN INSIDE THE REVIEW'S SIDE VIEW, and it is a binding
        // on the state's template rather than anything the snippet decides:
        // `hide-show-more-less-button="::true"` appears exactly twice in 3,000 lines of state
        // config, on this state and on the candidate one beside it. Rendering it here put a
        // button in the header that production does not have — 1,557 pixels at 700, the whole
        // difference between 0.99% and 0.25% on this screen.
        hideShowMoreLessButton={viaHunter}
        viewMore={viewMore}
        onToggleViewMore={onToggleViewMore}
        sideBySide={child !== null && !onMoreRoute}
        maxSnippetWidth={ExtensionViewWidth.Extended}
        maxWidth={viewMore ? ExtensionViewWidth.Extended + ExtensionViewWidth.Detailed : ExtensionViewWidth.Extended}
      >
        <div className={`shortlist-candidate-profile-view${viewMore ? ' rendered' : ''}`}>{viewMore ? child : null}</div>
      </ClientContactSnippet>
      <div className={`shortlist-candidate-profile-side-view${child === null || onMoreRoute ? '' : ' rendered'}`}>
        {onMoreRoute ? null : child}
      </div>
      {createInAts.modals}
      {exporting === undefined || contact === undefined ? null : (
        // ⚠️ MOUNTED ONLY FOR THE RUN, AND IT STARTS THE ATS WRITE ON MOUNT — see the component.
        <SingleProfileExportModal
          key={exporting.key}
          entity="clientContacts"
          id={contact.id}
          target={exporting.target}
          {...(atsName === undefined ? {} : { atsName })}
          onOpenInAts={sendOpenHotlist}
          onSettled={onExportSettled}
          onDismissed={() => {
            setExporting(undefined)
          }}
        />
      )}
    </div>
  )
}
